import React, {useContext, useEffect, useMemo, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {getWhiteLabelUserId} from '@vio/core/src/auth/modules/selectors'
import {getPreAuthAnalyticsData} from '@vio/core/src/auth/services'
import {getUrlParam} from '@vio/core/src/utils/url'

import {SusiContext} from '../../authentication/SUSI/bottomSheet/SusiBottomSheet/SusiProvider'
import {clearPendingEnable, showToast} from '../slice'
import {
  selectPendingEnable,
  selectPendingEnableTrackingContext,
  selectPriceTrendsTrackingContext
} from '../slice/selectors'
import {InitPriceWatchOptions, PriceWatchCTAParams} from '../types'
import {getCallbackUrl} from '../utils'
import {usePriceWatch} from './usePriceWatch'

const removeQueryParam = (param: string) => {
  const urlObject = new URL(location.href)
  const searchParams = new URLSearchParams(location.search)
  searchParams.delete(param)
  urlObject.search = searchParams.toString()
  history.replaceState(history.state, '', urlObject.toString())
}

export const usePriceWatchToggleLogic = (props: PriceWatchCTAParams) => {
  const {setCallbackUrl} = useContext(SusiContext)
  const {
    isAuthenticated,
    hotel,
    checkIn,
    checkOut,
    hotelContext,
    getSearchContext,
    setWebBottomSheetOpen,
    alertsManagementUrl,
    componentName,
    trackingContext,
    trackingPriceWatchId,
    saveFramingIsOn
  } = props
  const reduxTrackingContext = useSelector(selectPriceTrendsTrackingContext)
  const pendingEnableTrackingContext = useSelector(
    selectPendingEnableTrackingContext
  )
  // After social login redirect, Redux state is lost. Read from sessionStorage
  // where it was persisted by persistPreAuthContextForPostRedirect.
  const sessionStorageTrackingContext = useMemo(
    () =>
      getPreAuthAnalyticsData()?.priceInsightsContext as
        | Record<string, unknown>
        | undefined,
    []
  )
  // For click events: only prop (PriceInsightsToggle) or Redux (PriceTrend
  // bottom sheet open — handles DOM layering). These clear when PriceTrend
  // closes, so the toggle outside PriceTrend won't leak context.
  const clickTrackingContext = trackingContext ?? reduxTrackingContext

  // For post-auth events (PriceAlertEnable.Succeeded): also include fallbacks
  // that survive auth flows (pendingEnable for OTP, sessionStorage for social
  // login redirect). Uses a ref to avoid stale closures in the callback.
  const postAuthTrackingContext =
    trackingContext ??
    reduxTrackingContext ??
    pendingEnableTrackingContext ??
    sessionStorageTrackingContext
  const postAuthTrackingContextRef = useRef(postAuthTrackingContext)
  postAuthTrackingContextRef.current = postAuthTrackingContext
  const searchContext = useSelector(getSearchContext)
  const {auth} = useBrandCapabilities()
  const isAuthenticatedWithWhiteLabelUser = useSelector(getWhiteLabelUserId)

  const dispatch = useDispatch()

  const pendingEnable = useSelector(selectPendingEnable)

  const onPriceWatchInitialized = (options?: InitPriceWatchOptions) => {
    const {isAutomatic} = options ?? {}

    // Only show toast if user is authenticated (matches original isEnabledToTriggerToggle logic)
    // An automatic watch gets no toast: nothing the user did prompted it.
    if (isAuthenticated && !isAutomatic) {
      dispatch(
        showToast({
          hotel,
          checkIn,
          checkOut,
          enabled: true,
          shouldShowNavigationIcon: Boolean(alertsManagementUrl),
          alertsManagementUrl,
          saveFramingIsOn
        })
      )
    }

    const currentQueryParams = new URLSearchParams(location.search)
    const component = currentQueryParams.get('componentPriceWatch')

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.PriceAlertEnable,
        action: Action.Succeeded,
        team: Team.Retention,
        analyticsContext: {
          [AnalyticsContext.SearchContext]: searchContext,
          [AnalyticsContext.HotelContext]: hotelContext
        },
        payload: {
          isUserSignedIn: props.isAuthenticated,
          ...(isAutomatic ? {isAutomatic} : undefined),
          ...(postAuthTrackingContextRef.current
            ? {priceInsightsContext: postAuthTrackingContextRef.current}
            : undefined)
        },
        component: component || componentName
      })
    )

    if (component) {
      removeQueryParam('componentPriceWatch')
    }
  }

  const params = {
    ...props,
    onPriceWatchInitialized
  }

  const {
    priceWatchEntry,
    isPriceWatchOn,
    cancelPriceWatch,
    initPriceWatch,
    userPriceWatchTrackings,
    userPriceWatchTrackingsLoaded
  } = usePriceWatch(params)

  // The entry behind isPriceWatchOn is keyed without the room; the caller's
  // tracking-derived state wins so paint and click act on the same tracking
  const resolvedIsOn = props.isTrackingOn ?? isPriceWatchOn

  const handleClick = (event: React.SyntheticEvent<Element, Event>) => {
    event?.preventDefault()
    event?.stopPropagation()

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.PriceAlertToggle,
        action: Action.Clicked,
        team: Team.Retention,
        analyticsContext: {
          [AnalyticsContext.SearchContext]: searchContext,
          [AnalyticsContext.HotelContext]: hotelContext
        },
        payload: {
          isUserSignedIn: isAuthenticated,
          from: resolvedIsOn ? 'enabled' : 'disabled',
          to: !resolvedIsOn ? 'enabled' : 'disabled',
          ...(clickTrackingContext
            ? {priceInsightsContext: clickTrackingContext}
            : undefined)
        },
        component: componentName
      })
    )

    if (!isAuthenticated) {
      // Trigger the brand's own login control instead of opening SUSI.
      const {hasExternalLoginTrigger} = auth
      if (
        hasExternalLoginTrigger.enabled &&
        !isAuthenticatedWithWhiteLabelUser
      ) {
        const loginButton = document.getElementById(
          hasExternalLoginTrigger.config.triggerElementId
        )
        loginButton?.click()
        return
      }

      setCallbackUrl(
        getCallbackUrl({
          ...props,
          hotelId: hotel.objectID
        })
      )
      setWebBottomSheetOpen()

      return
    }

    if (isAuthenticated && !resolvedIsOn) {
      initPriceWatch()
      return
    }

    if (resolvedIsOn) {
      cancelPriceWatch({priceWatchId: trackingPriceWatchId})
      // Dispatch toast to Redux instead of local state
      dispatch(
        showToast({
          hotel,
          checkIn,
          checkOut,
          enabled: false,
          shouldShowNavigationIcon: Boolean(alertsManagementUrl),
          alertsManagementUrl,
          saveFramingIsOn
        })
      )
      return
    }
  }

  const hotelId = hotel?.objectID

  const enablePriceWatchAfterAuth = () => {
    if (!isAuthenticated) return

    // Handle enable via URL param (?enablePriceWatch=hotelId)
    const enablePriceWatchParam = getUrlParam({
      search: location.search,
      param: 'enablePriceWatch'
    })
    const currentQueryParams = new URLSearchParams(location.search)
    const componentParam = currentQueryParams.get('componentPriceWatch')
    const isUrlMatch = hotelId === enablePriceWatchParam
    const isEnableFromEmail =
      componentParam === 'emailExplicitCTA' &&
      // prevent triggering multiple times
      componentName === 'PriceWatchBell'

    const isComponentMatch = componentName === componentParam
    const isValidComponent = isEnableFromEmail || isComponentMatch

    if (isUrlMatch && isValidComponent && !isPriceWatchOn) {
      initPriceWatch()
      removeQueryParam('enablePriceWatch')
      return
    }

    // Handle enable via Redux (pendingEnable) -> Derived from OTP flow
    if (
      pendingEnable?.hotelId !== hotelId ||
      pendingEnable?.component !== componentName
    )
      return

    if (!isPriceWatchOn) {
      initPriceWatch()
    }

    if (
      isPriceWatchOn &&
      pendingEnable?.hotelId === hotelId &&
      isAuthenticated
    ) {
      // Dispatch toast to Redux instead of local state
      dispatch(
        showToast({
          hotel,
          checkIn,
          checkOut,
          enabled: true,
          shouldShowNavigationIcon: Boolean(alertsManagementUrl),
          alertsManagementUrl,
          saveFramingIsOn
        })
      )
    }

    dispatch(clearPendingEnable())
  }

  const disablePriceWatchViaURL = () => {
    const currentQueryParams = new URLSearchParams(location.search)
    const unsubscribePriceWatchParam = currentQueryParams.get(
      'unsubscribePriceWatch'
    )
    const hotelIdURL = currentQueryParams.get('hotelId')

    const shouldUnsubscribePriceWatch =
      unsubscribePriceWatchParam && hotelId === hotelIdURL

    if (shouldUnsubscribePriceWatch) {
      cancelPriceWatch()
      // Dispatch toast to Redux instead of local state
      dispatch(
        showToast({
          hotel,
          checkIn,
          checkOut,
          enabled: false,
          shouldShowNavigationIcon: Boolean(alertsManagementUrl),
          alertsManagementUrl,
          saveFramingIsOn
        })
      )
      removeQueryParam('unsubscribePriceWatch')
    }
  }

  useEffect(() => {
    // Re-evaluate when either the URL changes OR the user authentication status changes.
    // The latter is necessary for flows (e.g. One-Time-Password) that do not perform a full
    // redirect but still complete authentication in-place.
    enablePriceWatchAfterAuth()
    disablePriceWatchViaURL()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, isAuthenticated, pendingEnable, isPriceWatchOn])

  useEffect(() => {
    // Cleanup enablePriceWatch when unmount component
    return () => {
      const currentQueryParams = new URLSearchParams(location.search)
      if (currentQueryParams.get('enablePriceWatch')) {
        removeQueryParam('enablePriceWatch')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isToggleOn = resolvedIsOn && Boolean(isAuthenticated)

  return {
    handleClick,
    isToggleOn,
    priceWatchEntry,
    cancelPriceWatch,
    initPriceWatch,
    userPriceWatchTrackings,
    userPriceWatchTrackingsLoaded
  }
}
